import {
  Box,
  ChakraProvider,
  DarkMode,
  Footer as BaseFooter,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  theme,
  Typography,
  useColorMode,
  useDisclosure,
} from '@bnb-chain/space';
import { useRouter } from 'next/router';
import { CloseIcon, DescriptionIcon } from '@bnb-chain/icons';

import { useAppSelector } from '@/core/store/hooks';
import { colorModeManager, customTheme } from '@/core/theme/ThemeProvider';

export function Footer() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const router = useRouter();
  const { colorMode } = useColorMode();

  const footerMenus = useAppSelector((state) => state.common.footerMenus);
  const locale = useAppSelector((state) => state.i18n.locale);
  const messages = useAppSelector((state) => state.i18n.messages);
  const footerMedia = useAppSelector((state) => state.common.footerMedia);

  const showDisclaimer = () => {
    onOpen();
  };

  const content = (
    <Box
      sx={{
        fontSize: '14px',
        lineHeight: '20px',
        color: theme.colors[colorMode].text.secondary,
        'p, ul, ol': {
          mb: '0.8em',
        },
        b: {
          fontWeight: 700,
        },
        h2: {
          color: theme.colors[colorMode].text.primary,
          fontWeight: 700,
          mb: '0.2em',
        },
        'ul, ol': {
          listStylePosition: 'inside',
          mt: '-0.8em',
          li: {
            position: 'relative',
            listStyle: 'none',
            ml: '2em',
            _before: {
              position: 'absolute',
              content: '""',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              backgroundColor: theme.colors[colorMode].text.secondary,
              left: '-1em',
              top: '9px',
            },
          },
        },
      }}
    >
      <p>
        BNB Chain Bridge page provides access to various third-party bridge services (“
        <b>Bridge Services</b>”). By using the Bridge Services, you agree to the terms below and
        acknowledge that you have read, understood, and accepted the risks associated with the use
        of the Bridge Services.
      </p>

      <h2>Third-Party Responsibility:</h2>
      <p>
        BNB Chain provides this page as a platform to aggregate connections to third-party bridge
        partners (the “<b>Bridge Partners</b>”). BNB Chain does not own, operate, manage, or control
        the underlying smart contracts associated with the Bridge Services, nor the infrastructure,
        or operations of the Bridge Partners. BNB Chain Bridge does not control or manipulate
        users&apos; assets; it only routes them to the Bridge Partners, who process users&apos;
        assets via their smart contracts. The security and functionality of these smart contracts
        are the sole responsibility of the respective Bridge Partner.
      </p>
      <p>
        BNB Chain makes no representations, warranties, or guarantees, express or implied, regarding
        the security, performance, or reliability of the Bridge Partners or their smart contracts.
      </p>

      <h2>Risk Acknowledgment:</h2>
      <p>
        By using the Bridge Services, you acknowledge and accept that BNB Chain is not responsible
        for any risks, including but not limited to security vulnerabilities, bugs, or exploits,
        associated with the underlying smart contracts of the Bridge Partners.
      </p>
      <p>
        You expressly acknowledge and accept the inherent risks of interacting with blockchain
        technology, including but not limited to:
      </p>
      <ul>
        <li>smart contract vulnerabilities and exploits;</li>
        <li>market volatility or financial loss; and</li>
        <li>the irreversible nature of blockchain transactions.</li>
      </ul>

      <h2>No Liability:</h2>
      <p>
        BNB Chain shall not be held liable for any losses, damages, or claims arising from your use
        of the Bridge Services. This includes, but is not limited to, any losses, damages, or claims
        arising from or related to:
      </p>
      <ul>
        <li>
          vulnerabilities, bugs, or exploits in the underlying smart contracts or infrastructure of
          any Bridge Partner;
        </li>
        <li>
          user error, including but not limited to, incorrect wallet addresses, transaction
          mistakes, or misunderstanding of the Bridge Services;
        </li>
        <li>
          actions or omissions by Bridge Partners, including but not limited to security breaches,
          operational failures, or unauthorized access; and
        </li>
        <li>any other issues related to the Bridge Services.</li>
      </ul>

      <h2>No Endorsement or Warranty:</h2>
      <p>
        The inclusion of any Bridge Partner on BNB Chain Bridge page does not constitute an
        endorsement, guarantee, warranty, recommendation, or certification by BNB Chain. BNB Chain
        does not audit, verify, or guarantee the security, accuracy, or reliability of any Bridge
        Partner or their technology.
      </p>
      <p>
        You are solely responsible for conducting your own independent research and due diligence
        prior to using the Bridge Services or engaging with any Bridge Partner. This includes
        evaluating the risks, terms, and functionality of the Bridge Partner’s smart contracts and
        services.
      </p>

      <h2>No Financial Advice:</h2>
      <p>
        The information on this BNB Chain Bridge page does not constitute financial, investment,
        legal, trading advice, or any other sort of advice and you should not treat any of the
        website&apos;s content as such. BNB Chain does not recommend that any cryptocurrency should
        be bought, sold, or held by you. You are encouraged to consult with qualified advisors
        before making any decisions involving blockchain technology or cryptocurrencies.
      </p>
    </Box>
  );

  return (
    <ChakraProvider
      colorModeManager={colorModeManager}
      theme={{ ...customTheme, breakpoints: theme.breakpoints }}
    >
      <DarkMode>
        <Box position={'relative'}>
          <Modal isOpen={isOpen} onClose={onClose} isCentered autoFocus={false}>
            <ModalOverlay outline={'none'} />
            <ModalContent
              borderRadius={{ base: 0, md: theme.sizes['6'] }}
              maxW={{ md: '435px', base: '100%' }}
              maxH={{ md: '600px', base: '100%' }}
              mx={{ base: '0', md: '16px' }}
              sx={{
                '.chakra-modal__body': {
                  px: theme.sizes['5'],
                  py: 0,
                  my: theme.sizes['5'],
                  overflowBehavior: 'contain',
                },
              }}
            >
              <ModalHeader
                px={theme.sizes['5']}
                py={'18px'}
                pt={'18px'}
                borderBottom={`1px solid ${theme.colors[colorMode].border[3]}`}
              >
                <Typography textAlign={'center'} size={'xs'} variant={'heading'} fontWeight={500}>
                  Disclaimer
                </Typography>
              </ModalHeader>
              <CloseIcon
                cursor={'pointer'}
                color={'#8C8F9B'}
                boxSize={theme.sizes['6']}
                onClick={onClose}
                position={'absolute'}
                top={theme.sizes['5']}
                right={theme.sizes['5']}
                _hover={{ color: theme.colors[colorMode].text.secondary }}
              />
              <ModalBody overflow={'auto'}>{content}</ModalBody>
            </ModalContent>
          </Modal>

          <BaseFooter
            menus={footerMenus}
            locale={locale}
            messages={messages}
            pathname={router.asPath}
            media={footerMedia}
            localeSwitchable={false}
            compact
            extraActions={[
              {
                icon: <DescriptionIcon boxSize={theme.sizes['5']} />,
                text: 'Disclaimer',
                onClick: showDisclaimer,
              },
            ]}
          />
        </Box>
      </DarkMode>
    </ChakraProvider>
  );
}
